﻿import React, { useState, useEffect } from 'react';
import { IndCoupledPlasmaSpectBenchSheet } from '../models/ind-coupled-plasma-spect-bench-sheet';
import { AnalysisScopeType, BenchSheetTechnologyType, DocumentType, Entities, NoteType } from '../models/enums';
import { AnalysisDatum } from '../models/analysis-datum';
import { ItemBasicInformation } from '../models/item-basic-information';
import { DocumentationConstants } from '../store/documentation-constants';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import BenchSheetData from './BenchSheetData';
import { CrudFunctions } from '../utilities/crud-functions';
import { FieldUpdate } from '../models/field-update';
import NoteViewer from './NoteViewer';
import MinimumSampleSizeComponent from './MinimumSampleSizeComponent';
import SamplePreparationsComponent from './SamplePreparations/SamplePreparationsComponent';
import IcpInstrument from './IcpInstrument';
import BenchSheetToolbar from './BenchSheetToolbar';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import AuditHistoryComponent from './AuditHistoryComponent';
import StandardsViewer from './StandardsViewer';
import TicketLogEntryForm from './TicketLogEntryForm';
import { UserDataStore } from '../store/userDataStore';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import DmaFooter from './DmaFooter';

import { SelectBox } from 'devextreme-react/select-box';
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 1;

const icpBenchSheetDetectorTypes = [
    { id: 1, name: 'OES' },
    { id: 2, name: 'MS' }
];

export default function IndCoupledPlasmaSpecBenchSheetForm() {
    const [benchSheet, setBenchSheet] =
        useState<IndCoupledPlasmaSpectBenchSheet>();

    const [canEdit, setCanEdit] =
        useState<boolean>(false);

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>(false);

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>(false);

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>(false);

    const [gcColumnSearchDialogVisible, setGcColumnSearchDialogVisible] =
        useState<boolean>(false);

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [itemBasicInitInformation, setItemBasicInitInformation] =
        useState<ItemBasicInformation>();

    const [issuesFound, setIssuesFound] =
        useState<boolean>(false);

    const [dataLoaded, setDataLoaded] =
        useState<boolean>(false);


    useEffect(() => {
        setAuthorizations();
        getAllData();
    }, []);

    const parts = window.location.href.split('/');
    const benchSheetId = parts[parts.length - 1];

    const getTitle = () => {
        let title = "Inductively Coupled Plasma Spectroscopy";

        if (benchSheet && benchSheet.detectorType) {
            const matchingDetectorType = icpBenchSheetDetectorTypes.find(t => t.id === benchSheet.detectorType);

            if (matchingDetectorType)
                title = `${title} - ${matchingDetectorType.name}`;
        }

        return title;
    }

    key++;

    return (
        <div>
            <div className="pw-form-container">
                <div className="pw-content">
                    <BenchSheetToolbar title={getTitle()}
                        subtitle="Inductive Coupled Plasma"
                        auditRowPointer={benchSheetId} entity={Entities.IcpBenchSheets}
                        technologyId={BenchSheetTechnologyType.InductivelyCoupledPlasmaSpectroscopy}
                        documentationId={DocumentationConstants.ICPBenchSheet}
                        showItemInformation={showBenchSheetInformationDialog}
                        showTicketLog={showTicketLogEntryDialog} />

                    <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

                    {/*
                    <div className="lq-header pw-margin-top-05">
                        <div className="lq-header-title">ICP Instrument</div>
                        <div className="title-bar-spacer" />
                        <div>
                            <Button className="title-bar-button"
                                text="" icon="info" type="success"
                                hint="item information"
                            />
                        </div>
                    </div>
                    */}

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Detector Type</div>
                            <SelectBox id="detectorType" readOnly={!canEdit}
                                placeholder='type'
                                valueExpr='id' displayExpr='name'
                                className="asset-selectbox"
                                height='2.1em'
                                dataSource={icpBenchSheetDetectorTypes}
                                onValueChanged={onValueChanged}
                                value={benchSheet && benchSheet.detectorType ? benchSheet.detectorType : undefined} />
                        </div>
                    </div>

                    <div>
                        <IcpInstrument benchSheetId={benchSheetId} isReadOnly={!canEdit}
                            icpBenchSheetTypeId={benchSheet && benchSheet.detectorType ? benchSheet.detectorType : undefined} />
                    </div>

                    <div className="pw-margin-top-05 pw-margin-bottom-05">
                        <SamplePreparationsComponent benchSheetId={benchSheetId} diluent={true} />
                    </div>

                    {(benchSheet && benchSheet.scopeTypes && benchSheet.scopeTypes.includes(AnalysisScopeType.Homogeneity)) &&
                        <MinimumSampleSizeComponent benchSheetId={benchSheetId} />
                    }

                    <SamplePreparationsComponent benchSheetId={benchSheetId} diluent={false} />

                    {/* TODO: 
                        <div className="pw-comp-row-scroll-fixed-grid">
                            <IcpAnalysisData analysisData={benchSheet && benchSheet.analysisData ?
                                benchSheet.analysisData : []} readOnly={!canEdit} 
                                onAnalysisDatumAdded={onAnalysisDataAdded}
                                onAnalysisDataChanged={onAnalysisDataUpdated}
                                onAnalysisDataDeleted={onAnalysisDataDeleted} />
                        </div>
                        */}

                    <div className="pw-comp-row-scroll-fixed-grid">
                        <BenchSheetData parentId={benchSheetId}
                            readOnly={!canEdit} canDelete={true}
                            technology={BenchSheetTechnologyType.InductivelyCoupledPlasmaSpectroscopy}
                            documentType={DocumentType.General}
                            imageHandlerTitle="C of A Images" />
                    </div>

                    {/*<div>*/}
                    {/*    <StandardsViewer batchId={benchSheet && benchSheet.batchId ?*/}
                    {/*        benchSheet.batchId : undefined}*/}
                    {/*        technologyId={BenchSheetTechnologyType.InductivelyCoupledPlasmaSpectroscopy} />*/}
                    {/*</div>*/}

                    <div className="pw-comp-row-scroll-fixed-grid">
                        <NoteViewer height='12rem' title="Bench Sheet Notes" readOnly={!canEdit}
                            noteType={NoteType.BenchSheet} parentId={benchSheetId} />
                    </div>

                    <DmaFooter />

                    {itemBasicInformationDialogVisible &&
                        <DialogPopup visible={itemBasicInformationDialogVisible}
                            onHiding={hideItemBasicInformationDialog}
                            title="Item Basic Information"
                            width={'25rem'}
                            height={'21rem'}
                            dragEnabled={false}
                            resizeEnabled={false}>
                            <ItemBasicInformationDialog initInformation={itemBasicInitInformation}
                                onOkClick={hideItemBasicInformationDialog}
                                key={`IBI${key}`} />
                        </DialogPopup>
                    }

                    {ticketLogEntryDialogVisible &&
                        <DialogPopup visible={ticketLogEntryDialogVisible}
                            onHiding={hideTicketLogEntryDialog}
                            title="Ticket Log Entry"
                            width={'55rem'}
                            height={'45rem'}
                            dragEnabled={true}
                            resizeEnabled={true}>
                            <TicketLogEntryForm formName="ICP Bench Sheet" contextData={benchSheet}
                                onCancel={hideTicketLogEntryDialog}
                                onComplete={hideTicketLogEntryDialog}
                                key={`TLE${key}`} />
                        </DialogPopup>
                    }

                    {globalDialogVisible &&
                        <GlobalDialog metadata={globalDialogMetadata} />
                    }
                </div>
            </div>

            {auditInformationDialogVisible &&
                <DialogPopup visible={auditInformationDialogVisible}
                    onHiding={hideAuditInformationDialog}
                    title="Audit History"
                    minWidth={'50rem'}
                    height={'50rem'}
                    dragEnabled={true}
                    resizeEnabled={false}>
                    <AuditHistoryComponent entityNumber={Entities.BenchSheets}
                        technology={BenchSheetTechnologyType.InductivelyCoupledPlasmaSpectroscopy}
                        key={`AuditHistory${key}`}
                        showOtherIdColumn={true}
                        childData={true}
                        rowPointer={benchSheetId} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }


            {/*
                {this.state.instrumentBasicInformationDialogVisible &&
                    <DialogPopup visible={this.state.instrumentBasicInformationDialogVisible}
                        onHiding={this.hideInstrumentBasicInformationDialog}
                        title="Item Basic Information"
                        width={'25rem'}
                        height={'21rem'}
                        dragEnabled={false}
                        resizeEnabled={false}>
                        <ItemBasicInformationDialog initInformation={this.state.instrumentBasicInitInformation}
                            onOkClick={this.hideInstrumentBasicInformationDialog}
                            key={`INBI${this.key}`} />
                    </DialogPopup>
                }
                */}


            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="ICP Bench Sheet" contextData={benchSheet}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function setAuthorizations() {
        const user = UserDataStore.getCurrentUser();

        const canEditRoles = ["SA", "MF-A", "MF-L", "MF-T", "PP-A", "PP-L", "PP-T", "DIST-A", "BA-A", "QCP-A", "QCD-A", "RD-A", "RD-L", "RD-T"];
        const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

        setCanEdit(canEdit);
    }

    async function onValueChanged(e: any) {
        if (!dataLoaded)
            return;

        if (benchSheet && e.element && e.element.id) {
            const caller = e.element.id as string;
            const currentValue = benchSheet[caller];

            if (currentValue === e.value)
                return;
            else if (!currentValue && !e.value)
                return;
            else {
                setBenchSheet({ ...benchSheet, [caller]: e.value });

                let fieldUpdate: FieldUpdate = {
                    entity: "IcpBenchSheets",
                    field: caller,
                    value: e.value,
                    rowId: benchSheetId
                };

                await updateIcpBenchSheetField(fieldUpdate)
                    .then(() => {

                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Field");
                    });
            }
        }
    }

    /*
    function onIcpBenchSheetTypeChanged(e: any) {
        const matchingIcpBenchSheetType = icpBenchSheetDetectorTypes.find(b => b.id === e.value);
        setIcpBenchSheetType(matchingIcpBenchSheetType);
    }
    */

    async function addAnalysisDatum(analysisDatum: AnalysisDatum) {
        if (analysisDatum) {
            analysisDatum.benchSheetId = benchSheetId;

            await insertAnalysisDatum(analysisDatum)
                .then(() => {
                    getAnalysisData();
                    return;
                });
        }
    }

    async function onAnalysisDataAdded(analysisDatum: AnalysisDatum) {
        if (analysisDatum) {
            analysisDatum.benchSheetId = benchSheetId;
            if (analysisDatum.uomId)
                analysisDatum.uom = { id: analysisDatum.uomId };

            await insertAnalysisDatum(analysisDatum)
                .then(() => {
                    getAnalysisData();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Inserting Analysis Data");
                });
        }
    }

    async function onAnalysisDataUpdated(analysisDatum: AnalysisDatum) {
        if (analysisDatum) {
            if (analysisDatum.uomId)
                analysisDatum.uom = { id: analysisDatum.uomId };

            await updateAnalysisDatum(analysisDatum)
                .then(() => {
                    getAnalysisData();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Analysis Data");
                });
        }
    }

    async function onAnalysisDataDeleted(analysisDatumId: any) {
        if (analysisDatumId) {
            await deleteAnalysisDatum(analysisDatumId)
                .then(() => {
                    getAnalysisData();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Analysis Data");
                });
        }
    }

    // get functions

    async function getBenchSheet() {
        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then((data) => {
                console.log("BENCHSHEET:");
                console.log(data);

                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet Data");
            });
    }

    async function getAnalysisData() {
        await CrudFunctions.getData(`benchsheets/icpanalysisdatabybenchsheetid/${benchSheetId}`)
            .then(data => {
                let analysisData = data as AnalysisDatum[];

                analysisData.forEach((a) => {
                    if (a.uom) {
                        a.uomId = a.uom.id;
                        a.uomName = a.uom.name;
                    }
                });

                /* TODO:
                if (analysisData) {
                    setBenchSheet({ ...benchSheet, analysisData: analysisData });
                }
                */
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Analysis Data");
            });
    }

    // POST functions

    async function insertAnalysisDatum(analysisDatum: AnalysisDatum) {
        return CrudFunctions.postData("benchsheets/icpanalysisdatum", analysisDatum);
    }

    // PUT functions

    async function updateIcpBenchSheetField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/icpbenchsheetfield", fieldUpdate)
    }

    async function updateAnalysisDatum(analysisDatum: AnalysisDatum) {
        return CrudFunctions.putData("benchsheets/icpanalysisdatum", analysisDatum);
    }

    // DELETE functions

    async function deleteAnalysisDatum(analysisDataId: string) {
        return CrudFunctions.deleteData(`benchsheets/analysisdatum/${analysisDataId}`);
    }

    // show/hide

    function showGcColumnSearchDialog(e: any) {
        setGcColumnSearchDialogVisible(true);
    }

    function hideAuditInformationDialog() {
        setAuditInformationDialogVisible(false);
    }

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    async function showBenchSheetAuditInformationDialog() {
        setAuditInformationDialogVisible(true);
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            let itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: benchSheet.benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInitInformation(itemBasicInitInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showIssuesDialog() {
        //todo
    }

    /*
    showInstrumentInformationDialog() {
        if (this.state.benchSheet && this.state.benchSheet.instrument) {
            let itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: this.state.benchSheet.instrument.assetId,
                createTimestamp: this.state.benchSheet.instrument.createTimestamp,
                lastModifiedTimestamp: this.state.benchSheet.instrument.modifyTimestamp,
                createUser: this.state.benchSheet.instrument.createUser,
                modifyUser: this.state.benchSheet.instrument.modifyUser
            };

            this.setState({
                instrumentBasicInitInformation: itemBasicInitInformation,
                instrumentBasicInformationDialogVisible: true
            });
        }
    }

    hideInstrumentBasicInformationDialog() {
        this.setState({ instrumentBasicInformationDialogVisible: false });
    }
    */

    async function getAllData() {
        await Promise.all([
            getBenchSheet(),
            getAnalysisData(),
        ])
            .then(() => setDataLoaded(true));
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
